import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Reveal, Overline } from "@/components/motion";
import Placeholder from "@/components/Placeholder";
import Lightbox from "@/components/Lightbox";

const META = [
  ["Client / Firm", "client"],
  ["Role", "role"],
  ["Year", "year"],
  ["Deliverables", "deliverables"],
];

function GalleryImage({ item, onOpen, full }) {
  return (
    <button
      onClick={() => onOpen(item)}
      data-testid={`gallery-item-${item.alt?.slice(0, 20) || "image"}`}
      className={`group block w-full text-left overflow-hidden border border-border bg-muted ${
        full ? "aspect-[16/9]" : "aspect-[4/3]"
      }`}
      aria-label={`View ${item.alt || "gallery image"}`}
    >
      {item.src ? (
        <img
          src={fileUrl(item.src)}
          alt={item.alt || ""}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
      ) : (
        <div className="h-full w-full transition-transform duration-700 ease-out group-hover:scale-105">
          <Placeholder label={item.alt || "Gallery image"} className="h-full w-full" />
        </div>
      )}
    </button>
  );
}

function VideoEmbed({ url }) {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]+)/);
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (yt || vimeo) {
    const src = yt ? `https://www.youtube.com/embed/${yt[1]}` : `https://player.vimeo.com/video/${vimeo[1]}`;
    return (
      <div className="aspect-video border border-border" data-testid="video-embed">
        <iframe src={src} title="Project video" className="w-full h-full" loading="lazy" allowFullScreen allow="autoplay; fullscreen; picture-in-picture" />
      </div>
    );
  }
  return (
    <video controls className="w-full border border-border" data-testid="video-player" src={fileUrl(url)} />
  );
}

export default function CaseStudy() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setProject(null);
    setNotFound(false);
    api.get(`/projects/${slug}`).then((r) => setProject(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (project) {
      document.title = `${project.title} — Twinkle Rajan`;
      const desc = project.summary || `${project.title} — a case study by Twinkle Rajan, Senior Visual & Brand Designer.`;
      const setMeta = (name, content, isProp) => {
        let el = document.querySelector(isProp ? `meta[property="${name}"]` : `meta[name="${name}"]`);
        if (!el) {
          el = document.createElement("meta");
          el.setAttribute(isProp ? "property" : "name", name);
          document.head.appendChild(el);
        }
        el.setAttribute("content", content);
      };
      setMeta("description", desc);
      setMeta("og:title", `${project.title} — Twinkle Rajan`, true);
      setMeta("og:description", desc, true);
      setMeta("og:url", `https://twinklerajan.design/work/${project.slug}`, true);
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.setAttribute("href", `https://twinklerajan.design/work/${project.slug}`);
    }
    return () => {
      document.title = "Twinkle Rajan — Senior Visual & Brand Designer";
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.setAttribute("href", "https://twinklerajan.design/");
    };
  }, [project]);

  if (notFound) {
    return (
      <div className="pt-40 pb-40 text-center" data-testid="case-study-not-found">
        <p className="font-serif text-4xl">Project not found</p>
        <Link to="/work" className="mt-6 inline-block text-accent hover:underline">Back to work</Link>
      </div>
    );
  }
  if (!project) {
    return <div className="pt-40 pb-40 text-center text-muted-foreground font-mono text-sm" data-testid="case-study-loading">Loading…</div>;
  }

  const fullItems = project.gallery?.filter((g) => g.layout === "full") || [];
  const halfItems = project.gallery?.filter((g) => g.layout !== "full") || [];

  return (
    <div className="pt-16" data-testid="case-study-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pt-14 sm:pt-20">
        <Reveal>
          <Link to="/work" data-testid="case-study-back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors">
            <ArrowLeft size={15} /> All work
          </Link>
          <div className="mt-8 flex items-center gap-4">
            <Overline>{project.category}</Overline>
            {project.year && <span className="text-xs font-mono text-muted-foreground">{project.year}</span>}
          </div>
          <h1 className="mt-4 font-serif text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05]" data-testid="case-study-title">
            {project.title}
          </h1>
          {project.summary && (
            <p className="mt-6 max-w-2xl text-lg sm:text-xl text-foreground/70 leading-relaxed">{project.summary}</p>
          )}
        </Reveal>
        <Reveal delay={0.1} className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-border pt-8" data-testid="case-study-meta">
          {META.map(([label, key]) =>
            project[key] ? (
              <div key={key}>
                <p className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground">{label}</p>
                <p className="mt-2 text-sm font-medium">{project[key]}</p>
              </div>
            ) : null
          )}
        </Reveal>
      </section>

      {/* COVER */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 mt-14">
        <Reveal>
          <div className="overflow-hidden border border-border bg-muted aspect-[16/9]" data-testid="case-study-cover">
            {project.cover_image ? (
              <img src={fileUrl(project.cover_image)} alt={project.title} className="h-full w-full object-cover" />
            ) : (
              <Placeholder label={`${project.title} — hero image`} className="h-full w-full" />
            )}
          </div>
        </Reveal>
      </section>

      {/* BODY */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 mt-20 sm:mt-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 space-y-20">
          {project.challenge && (
            <Reveal>
              <Overline>01 — The challenge</Overline>
              <p className="mt-5 text-lg leading-relaxed text-foreground/80 max-w-2xl" data-testid="case-study-challenge">{project.challenge}</p>
            </Reveal>
          )}
          {project.role && (
            <Reveal>
              <Overline>02 — My role</Overline>
              <p className="mt-5 text-lg leading-relaxed text-foreground/80 max-w-2xl" data-testid="case-study-role">
                {project.role}{project.client ? ` — working with ${project.client}` : ""} across{" "}
                {project.deliverables || project.category.toLowerCase()}.
              </p>
            </Reveal>
          )}
          {project.approach?.length > 0 && (
            <Reveal>
              <Overline>03 — The approach</Overline>
              <div className="mt-6 space-y-6 max-w-2xl" data-testid="case-study-approach">
                {project.approach.map((a, i) => (
                  <div key={i} className="flex gap-5 border-t border-border pt-5">
                    <span className="font-mono text-xs text-accent mt-1">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-base leading-relaxed text-foreground/80">{a}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          )}
        </div>
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-28 border border-border p-8 bg-muted/40">
            <Overline>At a glance</Overline>
            <dl className="mt-6 space-y-5 text-sm">
              {META.map(([label, key]) =>
                project[key] ? (
                  <div key={key}>
                    <dt className="text-muted-foreground">{label}</dt>
                    <dd className="mt-1 font-medium">{project[key]}</dd>
                  </div>
                ) : null
              )}
            </dl>
          </div>
        </aside>
      </section>

      {/* GALLERY */}
      {(fullItems.length > 0 || halfItems.length > 0) && (
        <section className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 mt-24 sm:mt-32" data-testid="case-study-gallery">
          <Reveal>
            <Overline>Gallery</Overline>
          </Reveal>
          <div className="mt-8 space-y-8">
            {fullItems.map((g, i) => (
              <Reveal key={`f-${i}`}>
                <GalleryImage item={g} onOpen={setLightbox} full />
              </Reveal>
            ))}
            {halfItems.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {halfItems.map((g, i) => (
                  <Reveal key={`h-${i}`} delay={i * 0.08}>
                    <GalleryImage item={g} onOpen={setLightbox} />
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* VIDEO */}
      {project.video_url && (
        <section className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 mt-20" data-testid="case-study-video">
          <Reveal>
            <Overline>In motion</Overline>
            <div className="mt-8">
              <VideoEmbed url={project.video_url} />
            </div>
          </Reveal>
        </section>
      )}

      {/* NEXT PROJECT */}
      {project.next_project && (
        <section className="mt-24 sm:mt-32 border-t border-border" data-testid="next-project-section">
          <Link
            to={`/work/${project.next_project.slug}`}
            data-testid="next-project-link"
            className="group block max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-20 sm:py-28"
          >
            <Overline>Next project</Overline>
            <span className="mt-4 flex items-center justify-between gap-6">
              <span className="font-serif text-3xl sm:text-5xl lg:text-6xl tracking-tight transition-transform duration-500 group-hover:translate-x-3">
                {project.next_project.title}
              </span>
              <ArrowRight size={36} className="shrink-0 text-accent transition-transform duration-500 group-hover:translate-x-3" />
            </span>
          </Link>
        </section>
      )}

      <Lightbox item={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
